import i18n from "@/i18n.ts";
import { PerformanceReviewEditSidebar } from "@/pages/talent/performance-reviews/edit/components/PerformanceReviewEditSidebar.tsx";
import {
  PerformanceReviewEditPageProps,
  usePerformanceReviewEditPage,
} from "@/pages/talent/performance-reviews/edit/pages/performance-review-edit-pages.tsx";
import { PerformanceReviewTitleSlot } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewTitleSlot.tsx";
import { PerformanceReviewEditPageStep } from "@/pages/talent/performance-reviews/edit/performance-review-edit.ts";
import { usePerformanceReviewUpdateRatings } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { useReviewerTypeForms } from "@/queries/performance-reviews/use-reviewer-type-forms.query.ts";
import { ApiFormViewElement } from "@/types/forms/form-view.types.ts";
import { ApiFormElementType } from "@/types/forms/form.types.ts";
import {
  ApiPerformanceReviewUpdateRatings,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Checkbox,
  FullScreenPage,
  Skeleton,
  Stack,
  Switch,
  Title,
} from "@skailer-com/uikit";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

const buildPerformanceReviewEditRatingsSchema = () =>
  yup.object({
    is_same_rating: yup.boolean().required(),
    rating_form_element_ids: yup
      .array()
      .of(yup.number().required())
      .required(i18n.t("errors:field_required")),
    is_rating_all_scales: yup.boolean().required(),
    reviewer_type_ratings: yup
      .array()
      .of(
        yup.object({
          id: yup.number().required(),
          rating_form_element_ids: yup.array().of(yup.number().required()).required(),
          is_rating_all_scales: yup.boolean().required(),
        })
      )
      .required(),
    is_avg_reviewer_type_rating: yup.boolean().required(),
    included_in_ratings_reviewer_type_ids: yup
      .array()
      .of(yup.number().required())
      .required(i18n.t("errors:field_required")),
  });

export const PerformanceReviewEditRatingsPage = ({ review }: PerformanceReviewEditPageProps) => {
  const { t } = useTranslation();
  const { confirmExit, navigateToStep } = usePerformanceReviewEditPage();

  const updateRatings = usePerformanceReviewUpdateRatings();
  const { data: reviewerTypeForms, isLoading: isLoading } = useReviewerTypeForms(review.id);

  const referenceOrder = PerformanceReviewReviewerTypeItems().map((item) => item.id);
  const reviewerTypes = review.reviewer_types.slice().sort((a, b) => {
    const indexA = referenceOrder.indexOf(a.type);
    const indexB = referenceOrder.indexOf(b.type);
    const safe = (i: number) => (i === -1 ? 999 : i);
    return safe(indexA) - safe(indexB);
  });

  const performanceReviewEditRatingsSchema = buildPerformanceReviewEditRatingsSchema();

  const { control, watch, handleSubmit } = useForm<ApiPerformanceReviewUpdateRatings>({
    mode: "onSubmit",
    resolver: yupResolver(performanceReviewEditRatingsSchema),
    defaultValues: {
      is_same_rating: review.is_same_rating,
      rating_form_element_ids: review.rating_form_element_ids,
      is_rating_all_scales: review.is_rating_all_scales,
      reviewer_type_ratings: reviewerTypes.map((type) => ({
        id: type.id,
        rating_form_element_ids: type.rating_form_element_ids,
        is_rating_all_scales: type.is_rating_all_scales,
      })),
      is_avg_reviewer_type_rating: review.is_avg_reviewer_type_rating,
      included_in_ratings_reviewer_type_ids: review.included_in_ratings_reviewer_type_ids,
    },
  });

  const isSameRating = watch("is_same_rating");
  const isRatingAllScales = watch("is_rating_all_scales");
  const isAvgReviewerTypeRating = watch("is_avg_reviewer_type_rating");
  const reviewerTypeRatings = watch("reviewer_type_ratings");

  const { scalesOverall, scalesByForm } = useMemo(() => {
    const scalesOverall: Record<string, ApiFormViewElement[]> = {};
    const scalesByForm: Record<number, ApiFormViewElement[]> = {};

    if (!reviewerTypeForms)
      return {
        scalesOverall: Object.values(scalesOverall),
        scalesByForm,
      };

    for (const form of reviewerTypeForms) {
      scalesByForm[form.id] = [];

      for (const page of form.pages) {
        for (const section of page.sections) {
          for (const element of section.elements) {
            if (element.type === ApiFormElementType.scale && element.scale_settings?.title) {
              const title = element.scale_settings.title;
              if (!scalesOverall[title]) {
                scalesOverall[title] = [];
              }
              scalesOverall[title].push(element);

              if (!scalesByForm[form.id]) {
                scalesByForm[form.id] = [];
              }
              scalesByForm[form.id].push(element);
            } else if (element.type === ApiFormElementType.goals && element.goals_settings?.title) {
              const title = element.goals_settings.title;
              if (!scalesOverall[title]) {
                scalesOverall[title] = [];
              }
              scalesOverall[title].push(element);

              if (!scalesByForm[form.id]) {
                scalesByForm[form.id] = [];
              }
              scalesByForm[form.id].push(element);
            }
          }
        }
      }
    }

    return {
      scalesOverall: Object.values(scalesOverall),
      scalesByForm,
    };
  }, [reviewerTypeForms]);

  const onSubmit = handleSubmit(async (data) => {
    let rating_form_element_ids = data.rating_form_element_ids;
    let reviewer_type_ratings = data.reviewer_type_ratings;
    let is_rating_all_scales = data.is_rating_all_scales;
    let included_in_ratings_reviewer_type_ids = data.included_in_ratings_reviewer_type_ids;

    if (data.is_same_rating) {
      reviewer_type_ratings = [];

      if (is_rating_all_scales) {
        rating_form_element_ids = [];
      }
    } else {
      is_rating_all_scales = false;
      rating_form_element_ids = [];
    }

    if (data.is_avg_reviewer_type_rating) {
      included_in_ratings_reviewer_type_ids = [];
    }

    try {
      await updateRatings.mutateAsync({
        id: review.id,
        payload: {
          is_same_rating: data.is_same_rating,
          rating_form_element_ids: rating_form_element_ids,
          is_rating_all_scales: is_rating_all_scales,
          reviewer_type_ratings: reviewer_type_ratings.map((item) => ({
            ...item,
            rating_form_element_ids: item.is_rating_all_scales ? [] : item.rating_form_element_ids,
          })),
          is_avg_reviewer_type_rating: data.is_avg_reviewer_type_rating,
          included_in_ratings_reviewer_type_ids: included_in_ratings_reviewer_type_ids,
        },
      });

      navigateToStep(review.id, PerformanceReviewEditPageStep.notifications);
    } catch {}
  });

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit(review.id)}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <>
          <Button
            variant={"secondary"}
            size={"lg"}
            onClick={() => {
              navigateToStep(review.id, PerformanceReviewEditPageStep.invitation);
            }}
          >
            {t("common:back")}
          </Button>
          <Button size={"lg"} onClick={onSubmit}>
            {t("common:continue")}
          </Button>
        </>
      }
      titleSlot={
        <PerformanceReviewTitleSlot step={PerformanceReviewEditPageStep.ratings} review={review} />
      }
      isCenterVertical={false}
      sidebarSlot={
        <PerformanceReviewEditSidebar
          step={PerformanceReviewEditPageStep.ratings}
          review={review}
        />
      }
    >
      <Stack gap={"sm"} justify={"center"}>
        <Stack className={"card-panel"} gap={"xl"}>
          <Title
            header={t("performance_review:ratings")}
            caption={t("performance_review:ratings_description")}
            size={"lg"}
          />
          <Controller
            control={control}
            name={"is_same_rating"}
            render={({ field }) => (
              <Switch
                checked={field.value}
                onChange={() => field.onChange(!field.value)}
                title={t("performance_review:same_ratings_reviewer_types")}
              />
            )}
          />
        </Stack>

        {isSameRating ? (
          <Stack gap={"sm"} justify={"center"}>
            <Stack key={`reviewer-item-overall`} className={"card-panel"} gap={"xl"}>
              <Title header={t("performance_review:overall_ratings")} />
              {isLoading ? (
                <Stack gap={"sm"}>
                  <Skeleton className={"w-auto h-10"} />
                  <Skeleton className={"w-auto h-10"} />
                  <Skeleton className={"w-auto h-10"} />
                  <Skeleton className={"w-auto h-10"} />
                </Stack>
              ) : (
                <Stack gap={"md"}>
                  <Controller
                    control={control}
                    name={"is_rating_all_scales"}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={() => field.onChange(!field.value)}
                        title={t("performance_review:all_questions")}
                        description={t("plurals:question_count", { count: scalesOverall.length })}
                      />
                    )}
                  />
                  {!isRatingAllScales && (
                    <Controller
                      control={control}
                      name="rating_form_element_ids"
                      render={({ field }) => {
                        const selectedIds = field.value ?? [];

                        const handleToggle = (ids: number[]) => {
                          const isChecked = ids.every((id) => selectedIds.includes(id));
                          const newValue = isChecked
                            ? selectedIds.filter((id) => !ids.includes(id))
                            : [...selectedIds, ...ids.filter((id) => !selectedIds.includes(id))];

                          field.onChange(newValue);
                        };

                        return (
                          <Stack gap="sm">
                            {scalesOverall.map((elements) => {
                              let title = elements[0]?.scale_settings?.title ?? "";
                              const ids = elements.map((el) => el.id);
                              const isChecked = ids.every((id) => selectedIds.includes(id));

                              if (elements[0]?.type === ApiFormElementType.goals) {
                                title = elements[0]?.goals_settings?.title ?? "";
                              }

                              return (
                                <Checkbox
                                  key={title}
                                  title={title}
                                  variant={"accent"}
                                  checked={isChecked}
                                  onChange={() => handleToggle(ids)}
                                />
                              );
                            })}
                          </Stack>
                        );
                      }}
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        ) : (
          reviewerTypes.map((type, index) => {
            const scales = type.form?.id !== undefined ? (scalesByForm[type.form.id] ?? []) : [];

            return (
              <Stack
                key={`reviewer-item-${type.type}-${type.id}`}
                className={"card-panel"}
                gap={"xl"}
              >
                <Title
                  header={
                    PerformanceReviewReviewerTypeItems().find((x) => x.id == type.type)?.title ??
                    type.title ??
                    ""
                  }
                />
                {isLoading ? (
                  <Stack gap={"sm"}>
                    <Skeleton className={"w-auto h-10"} />
                    <Skeleton className={"w-auto h-10"} />
                    <Skeleton className={"w-auto h-10"} />
                    <Skeleton className={"w-auto h-10"} />
                  </Stack>
                ) : (
                  <Stack gap={"md"}>
                    <Controller
                      control={control}
                      name={`reviewer_type_ratings.${index}.is_rating_all_scales`}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={() => field.onChange(!field.value)}
                          title={t("performance_review:all_questions")}
                          description={t("plurals:question_count", { count: scales.length })}
                        />
                      )}
                    />

                    {!reviewerTypeRatings?.[index]?.is_rating_all_scales && (
                      <Controller
                        name={`reviewer_type_ratings.${index}.rating_form_element_ids`}
                        control={control}
                        render={({ field }) => {
                          const selectedIds = field.value ?? [];

                          const handleToggle = (id: number) => {
                            const isChecked = selectedIds.includes(id);
                            const newValue = isChecked
                              ? selectedIds.filter((item) => item !== id)
                              : [...selectedIds, id];

                            field.onChange(newValue);
                          };

                          return (
                            <Stack gap="sm">
                              {scales.map((element) => {
                                let title = element?.scale_settings?.title ?? "";

                                if (element.type === ApiFormElementType.goals) {
                                  title = element?.goals_settings?.title ?? "";
                                }

                                const isChecked = selectedIds.includes(element.id);

                                return (
                                  <Checkbox
                                    key={title}
                                    title={title}
                                    variant={"accent"}
                                    checked={isChecked}
                                    onChange={() => handleToggle(element.id)}
                                  />
                                );
                              })}
                            </Stack>
                          );
                        }}
                      />
                    )}
                  </Stack>
                )}
              </Stack>
            );
          })
        )}

        <Stack key={`reviewer-item-weight`} className={"card-panel"} gap={"xl"}>
          <Title header={t("performance_review:final_score_breakdown")} />
          {isLoading ? (
            <Stack gap={"sm"}>
              <Skeleton className={"w-auto h-10"} />
              <Skeleton className={"w-auto h-10"} />
              <Skeleton className={"w-auto h-10"} />
              <Skeleton className={"w-auto h-10"} />
            </Stack>
          ) : (
            <Stack gap={"md"}>
              <Controller
                control={control}
                name={"is_avg_reviewer_type_rating"}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onChange={() => field.onChange(!field.value)}
                    title={t("performance_review:avg_by_all_reviewer_types")}
                    description={t("plurals:reviewer_groups_count", {
                      count: reviewerTypes.length,
                    })}
                  />
                )}
              />
              {!isAvgReviewerTypeRating && (
                <Controller
                  control={control}
                  name="included_in_ratings_reviewer_type_ids"
                  render={({ field }) => {
                    const selectedIds = field.value ?? [];

                    const handleToggle = (ids: number[]) => {
                      const isChecked = ids.every((id) => selectedIds.includes(id));
                      const newValue = isChecked
                        ? selectedIds.filter((id) => !ids.includes(id))
                        : [...selectedIds, ...ids.filter((id) => !selectedIds.includes(id))];

                      field.onChange(newValue);
                    };

                    return (
                      <Stack gap="sm">
                        {reviewerTypes.map((type) => {
                          const title =
                            PerformanceReviewReviewerTypeItems().find((x) => x.id == type.type)
                              ?.title ??
                            type.title ??
                            "";
                          const id = type.id;
                          const isChecked = selectedIds.includes(id);

                          return (
                            <Checkbox
                              key={id}
                              title={title}
                              variant={"accent"}
                              checked={isChecked}
                              onChange={() => handleToggle([id])}
                            />
                          );
                        })}
                      </Stack>
                    );
                  }}
                />
              )}
            </Stack>
          )}
        </Stack>

        <Button onClick={onSubmit} size={"lg"} isLoading={updateRatings.isPending}>
          {t("common:continue")}
        </Button>
      </Stack>
    </FullScreenPage>
  );
};
